// src/pages/ApplyProvider.tsx: public service provider application (no auth required).
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import LocationSelect from '../components/LocationSelect';
import { C, HEARD_ABOUT, generatePassword, sendMail } from '../lib/applications';
import { uploadAsset } from '../lib/serviceConnect';
import {
  PROVIDER_CATEGORIES, PROVIDER_REGIONS, TARGET_CLIENT_TYPES, PRICING_MODELS,
  PROVIDER_FROM_PRICE, pricingModelLabel, clientTypeLabels,
} from '../lib/onboardingApplications';
import {
  ApplyShell, ProgressRail, SubmittedScreen, Field, Row, Section, CheckboxGroup,
  YesNo, InfoBox, ErrorBanner, card, inputStyle, labelStyle, btnPrimary, btnGhost, stepHeading,
} from '../components/ApplyShell';

const STEPS = ['Contact Details', 'Company', 'Services', 'Listing', 'Review & Submit'];

const REGION_OPTIONS = PROVIDER_REGIONS.map(r => ({ value: r, label: r }));

export default function ApplyProvider() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1
  const [contactName, setContactName]   = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [heardAbout, setHeardAbout]     = useState('');

  // Step 2
  const [companyName, setCompanyName] = useState('');
  const [category, setCategory]       = useState('');
  const [country, setCountry]         = useState('');
  const [city, setCity]               = useState('');
  const [website, setWebsite]         = useState('');
  const [foundedYear, setFoundedYear] = useState('');
  const [clientCount, setClientCount] = useState('');
  const [regions, setRegions]         = useState<string[]>([]);
  const [description, setDescription] = useState('');

  // Step 3
  const [primaryServices, setPrimaryServices] = useState('');
  const [clientTypes, setClientTypes]         = useState<string[]>([]);
  const [pricingModel, setPricingModel]       = useState('');

  // Step 4
  const [logoFile, setLogoFile]     = useState<File | null>(null);
  const [hasVideo, setHasVideo]     = useState(false);
  const [videoUrl, setVideoUrl]     = useState('');

  // Step 5
  const [confirmAccurate, setConfirmAccurate] = useState(false);
  const [confirmTerms, setConfirmTerms]       = useState(false);

  const toggle = (list: string[], set: (v: string[]) => void) => (v: string) =>
    set(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  const step1Valid = contactName.trim() && contactEmail.trim();
  const step2Valid = companyName.trim() && category && description.trim();

  const next = () => { setError(''); setStep(s => Math.min(5, s + 1)); };
  const back = () => { setError(''); setStep(s => Math.max(1, s - 1)); };

  const submit = async () => {
    setSubmitting(true); setError('');
    const email = contactEmail.trim().toLowerCase();
    try {
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email,
        password: generatePassword(),
        options: {
          data: { full_name: contactName.trim(), role: 'service_provider' },
          emailRedirectTo: `${window.location.origin}/application-status?type=provider`,
        },
      });
      if (signUpErr && !/already registered/i.test(signUpErr.message)) throw signUpErr;

      const profileId = signUpData?.user?.id
        || (await supabase.auth.getUser()).data.user?.id
        || null;
      if (!profileId) {
        throw new Error('This email already has an account. Please sign in first, then reapply.');
      }

      // A logo failure must not lose the application, so it degrades to no logo.
      let logoPath: string | null = null;
      if (logoFile) {
        try {
          logoPath = await uploadAsset(profileId, 'logo', logoFile);
        } catch (e) {
          console.error('Logo upload failed:', e);
        }
      }

      const { error: appErr } = await supabase.from('service_provider_applications').insert({
        contact_name: contactName.trim(),
        contact_email: email,
        contact_phone: contactPhone.trim() || null,
        heard_about: heardAbout || null,
        company_name: companyName.trim(),
        category,
        company_description: description.trim() || null,
        company_website: website.trim() || null,
        headquarters_country: country || null,
        headquarters_city: city || null,
        founded_year: foundedYear ? parseInt(foundedYear) : null,
        client_count: clientCount ? parseInt(clientCount) : null,
        regions_covered: regions,
        primary_services: primaryServices.trim() || null,
        target_client_types: clientTypes,
        pricing_model: pricingModel || null,
        logo_url: logoPath,
        demo_video_url: hasVideo ? (videoUrl.trim() || null) : null,
        status: 'submitted',
        profile_id: profileId,
      });
      if (appErr) throw appErr;

      await sendMail('provider_application_received', {
        contact_email: email, contact_name: contactName.trim(),
        company_name: companyName.trim(), category,
        regions: regions.join(', '),
      });

      setSubmitted(true);
    } catch (e: any) {
      setError(e?.message || 'Could not submit your application. Please try again.');
    }
    setSubmitting(false);
  };

  const shell = (children: React.ReactNode) => (
    <ApplyShell
      title="Apply as a Service Provider"
      subtitle="List your firm on the Altveno marketplace"
    >{children}</ApplyShell>
  );

  if (submitted) return shell(
    <SubmittedScreen
      email={contactEmail}
      timeline="Our team will review your application and be in touch about pricing within 2 business days."
      onContinue={() => navigate('/application-status?type=provider')}
    />
  );

  return shell(
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '28px 24px 64px' }}>
      <ProgressRail steps={STEPS} step={step} />
      {error && <ErrorBanner text={error} />}

      {/* ── Step 1 ── */}
      {step === 1 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Contact details</h2>
          <Field label="Full name *">
            <input style={inputStyle} value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Jane Smith" />
          </Field>
          <Field label="Email address *">
            <input style={inputStyle} type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="jane@yourcompany.com" />
          </Field>
          <Field label="Phone number (optional)">
            <input style={inputStyle} value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+44 20 1234 5678" />
          </Field>
          <Field label="How did you hear about us?">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={heardAbout} onChange={e => setHeardAbout(e.target.value)}>
              <option value="">Select…</option>
              {HEARD_ABOUT.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px' }}>
            <button style={{ ...btnPrimary, opacity: step1Valid ? 1 : 0.5 }} disabled={!step1Valid} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 2 ── */}
      {step === 2 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Company details</h2>
          <Field label="Company name *">
            <input style={inputStyle} value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Halstead & Reyes LLP" />
          </Field>
          <Field label="Category *">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={category} onChange={e => setCategory(e.target.value)}>
              <option value="">Select category…</option>
              {PROVIDER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <div style={{ marginBottom: '14px' }}>
            <LocationSelect
              country={country} city={city}
              onCountryChange={setCountry} onCityChange={setCity}
              countryLabel="Headquarters country" cityLabel="Headquarters city"
              labelStyle={labelStyle}
            />
          </div>
          <Field label="Website" hint="Recommended, allocators and managers use it to check you out.">
            <input style={inputStyle} value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://yourcompany.com" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Year founded">
              <input style={inputStyle} type="number" value={foundedYear} onChange={e => setFoundedYear(e.target.value)} placeholder="2004" />
            </Field>
            <Field label="Number of clients">
              <input style={inputStyle} type="number" value={clientCount} onChange={e => setClientCount(e.target.value)} placeholder="120" />
            </Field>
          </div>
          <Field label="Regions covered">
            <CheckboxGroup options={REGION_OPTIONS} selected={regions} onToggle={toggle(regions, setRegions)} columns={3} />
          </Field>
          <Field label="Brief company description *">
            <textarea style={{ ...inputStyle, height: '100px', resize: 'vertical', lineHeight: 1.6 }}
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What your firm does and who it serves..." />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={{ ...btnPrimary, opacity: step2Valid ? 1 : 0.5 }} disabled={!step2Valid} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 3 ── */}
      {step === 3 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Services and offering</h2>
          <Field label="Primary services"
            hint="Describe the main services you offer to alternative investment managers and allocators.">
            <textarea style={{ ...inputStyle, height: '120px', resize: 'vertical', lineHeight: 1.6 }}
              value={primaryServices} onChange={e => setPrimaryServices(e.target.value)} />
          </Field>
          <Field label="Target client types">
            <CheckboxGroup options={TARGET_CLIENT_TYPES} selected={clientTypes} onToggle={toggle(clientTypes, setClientTypes)} columns={3} />
          </Field>
          <Field label="Pricing model">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={pricingModel} onChange={e => setPricingModel(e.target.value)}>
              <option value="">Select model…</option>
              {PRICING_MODELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={btnPrimary} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 4 ── */}
      {step === 4 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Listing details</h2>
          <InfoBox>
            Service provider listings on Altveno start from {PROVIDER_FROM_PRICE} per year. Our team
            will discuss pricing options with you as part of the review process.
          </InfoBox>

          <Field label="Company logo (optional)" hint="PNG or JPG, shown on your marketplace listing.">
            <input type="file" accept="image/*" style={{ ...inputStyle, padding: '7px 9px' }}
              onChange={e => setLogoFile(e.target.files?.[0] || null)} />
            {logoFile && (
              <div style={{ fontSize: '12px', color: C.success, marginTop: '6px' }}>Selected: {logoFile.name}</div>
            )}
          </Field>

          <Field label="Do you have a demo video?">
            <YesNo value={hasVideo} onChange={setHasVideo} />
          </Field>

          {hasVideo && (
            <Field label="Demo video link" hint="YouTube, Vimeo or a direct URL.">
              <input style={inputStyle} value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
                placeholder="https://vimeo.com/..." />
            </Field>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={btnPrimary} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 5 ── */}
      {step === 5 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Review and submit</h2>

          <Section title="Contact">
            <Row k="Name" v={contactName} />
            <Row k="Email" v={contactEmail} />
            <Row k="Phone" v={contactPhone} />
          </Section>

          <Section title="Company">
            <Row k="Company name" v={companyName} />
            <Row k="Category" v={category} />
            <Row k="Headquarters" v={[city, country].filter(Boolean).join(', ')} />
            <Row k="Website" v={website} />
            <Row k="Founded" v={foundedYear} />
            <Row k="Clients" v={clientCount} />
            <Row k="Regions covered" v={regions.join(', ')} />
          </Section>

          <Section title="Services">
            <Row k="Target clients" v={clientTypeLabels(clientTypes)} />
            <Row k="Pricing model" v={pricingModelLabel(pricingModel)} />
          </Section>

          <Section title="Listing">
            <Row k="Logo" v={logoFile ? logoFile.name : 'Not provided'} />
            <Row k="Demo video" v={hasVideo ? (videoUrl || 'Yes, link to follow') : 'No'} />
          </Section>

          <div style={{ marginTop: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
              <input type="checkbox" checked={confirmAccurate} onChange={e => setConfirmAccurate(e.target.checked)} style={{ marginTop: '2px' }} />
              I confirm that all information provided is accurate and I am authorised to apply on behalf of {companyName || 'my company'}
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
              <input type="checkbox" checked={confirmTerms} onChange={e => setConfirmTerms(e.target.checked)} style={{ marginTop: '2px' }} />
              <span>
                I agree to the Altveno{' '}
                <a href="/terms" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Terms of Service</a>{' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Privacy Policy</a>
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '22px' }}>
            <button style={btnGhost} onClick={back} disabled={submitting}>Back</button>
            <button
              style={{ ...btnPrimary, opacity: confirmAccurate && confirmTerms && !submitting ? 1 : 0.5 }}
              disabled={!confirmAccurate || !confirmTerms || submitting}
              onClick={submit}
            >{submitting ? 'Submitting…' : 'Submit application'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
